import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Wordmark } from "@/components/site/Logo";
import { InquiryList } from "@/components/admin/InquiryList";
import { Dashboard } from "@/components/admin/Dashboard";
import { Vehicles } from "@/components/admin/Vehicles";
import { Customers } from "@/components/admin/Customers";
import { WorkOrders } from "@/components/admin/WorkOrders";
import { Settings } from "@/components/admin/Settings";
import { Prices } from "@/components/admin/Prices";

const TABS = [["dashboard", "Oversikt"], ["vehicles", "Biloversikt"], ["customers", "Kunderegister"], ["orders", "Arbeidsordre"], ["inquiries", "Forespørsler"], ["prices", "Priser"], ["settings", "Innstillinger"]];

const Panel = () => {
  const [tab, setTab] = useState("dashboard");
  const [openOrder, setOpenOrder] = useState(null);
  const clearOpen = useCallback(() => setOpenOrder(null), []);
  const go = (t, orderId) => { setTab(t); if (orderId) setOpenOrder(orderId); window.scrollTo(0, 0); };
  return (
    <>
      <nav className="sticky top-0 z-40 border-b border-white/10 bg-[#08090b]/95 backdrop-blur" data-testid="admin-tabs">
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-5 sm:px-8">
          {TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => go(k)} data-testid={`admin-tab-${k}`}
              className={`whitespace-nowrap border-b-2 px-3 py-3.5 font-mono text-xs uppercase tracking-widest ${tab === k ? "border-[var(--red)] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </nav>
      {tab === "inquiries" ? <InquiryList onOpenOrder={(id) => go("orders", id)} /> : (
        <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
          {tab === "dashboard" && <Dashboard go={go} />}
          {tab === "vehicles" && <Vehicles />}
          {tab === "customers" && <Customers />}
          {tab === "orders" && <WorkOrders openId={openOrder} clearOpen={clearOpen} />}
          {tab === "settings" && <Settings />}
          {tab === "prices" && <Prices />}
        </main>
      )}
    </>
  );
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (x) {
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  };
  const cls = "w-full border border-white/15 bg-transparent px-4 py-3 text-white outline-none focus:border-[var(--red)]";
  return (
    <div className="grid min-h-screen place-items-center px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-white/10 bg-[var(--surface)] p-8">
        <Wordmark />
        <h1 className="mt-8 font-display text-4xl font-black uppercase">Admin</h1>
        <p className="mb-6 text-sm text-zinc-400">Logg inn for å se forespørsler.</p>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-post" className={`${cls} mb-3`} data-testid="admin-email-input" />
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Passord" className={cls} data-testid="admin-password-input" />
        {err && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{err}</p>}
        <button disabled={busy} data-testid="admin-login-submit" className="mt-6 flex w-full items-center justify-center gap-2 bg-[var(--red)] py-3 font-mono text-sm font-bold uppercase tracking-widest disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Logg inn
        </button>
        <Link to="/" className="mt-6 flex items-center gap-2 text-xs text-zinc-400 hover:text-white" data-testid="admin-back-link"><ArrowLeft size={14} /> Til nettsiden</Link>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/session").then((r) => setUser(r.data?.user ?? false)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    toast.success("Logget ut");
  };
  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="animate-spin text-zinc-400" /></div>;
  if (!user) return <Login onLogin={setUser} />;
  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[var(--surface)]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-home-link"><Wordmark /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs text-zinc-400 sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-xs uppercase hover:border-white/40">
              <LogOut size={14} /> Logg ut
            </button>
          </div>
        </div>
      </header>
      <Panel />
    </div>
  );
}
